import { useState, useEffect, useCallback, useContext } from "react"
import { useNavigate } from "react-router-dom"
import classes from "./Management.module.scss"
import { getUsers, verifyUser, updateUser, updateUserDeposit } from "../../services"
import AuthContext from "../../store/auth-context"
import paginationStyles from "./Pagination.module.scss"

const UserManagement = () => {
  // "Open" shows the customer's page on Dealer Stock (Management.jsx switches tab).
  const navigate = useNavigate()
  const openCustomer = (user) => navigate(`/management?customer=${user._id}`)
  // Example data
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const authCtx = useContext(AuthContext)

  const [searchTerm, setSearchTerm] = useState("")
  const [editUserId, setEditUserId] = useState(null)
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    isVerified: false,
    isVipPlus: false,
    deposit: 0
  })
 
  // Search functionality

  // Handle edit user
  const handleEditUser = (user) => {
    setEditUserId(user._id)
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      isVerified: user.isVerified,
      isVipPlus: user.isVipPlus || false,
      // Handle both object and number formats for deposit
      deposit: user.deposit?.amount || user.deposit || 0
    })
  }

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({ 
      ...editFormData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  }

  const handleSaveChanges = (e) => {
    // Prevent default form submission behavior
    if (e) e.preventDefault();
    
    let user = users.find((user) => user._id === editUserId)
    
    // Check if deposit amount has changed
    const oldDepositAmount = getDepositAmount(user.deposit);
    const newDepositAmount = parseFloat(editFormData.deposit) || 0;
    
    if (oldDepositAmount !== newDepositAmount) {
      // Calculate whether we're adding or removing deposit
      const depositAction = newDepositAmount > oldDepositAmount ? 'add' : 'remove';
      const depositAmount = Math.abs(newDepositAmount - oldDepositAmount);
      
      // Update deposit amount
      updateUserDeposit({
        depositAmount: depositAmount,
        depositAction: depositAction,
        notes: `Manual update by admin on ${new Date().toLocaleDateString()}`
      }, editUserId).then((res) => {
        if (res && res.message === "User deposit updated successfully!") {
          console.log("Deposit updated successfully");
          // Refresh user data to show updated deposit
          getUsersData();
        } else {
          console.error("Deposit update failed:", res);
          alert("Failed to update deposit. Please try again.");
        }
      }).catch(error => {
        console.error("Error updating deposit:", error);
        alert("Error updating deposit. Please try again.");
      });
    }
    
    // Original code for handling other changes.
    // Tier and name updates are independent — both must fire when both changed.
    if(editFormData && editFormData.email && (editFormData.isVerified !== user.isVerified || editFormData.isVipPlus !== (user.isVipPlus || false))){
      verifyUser({
        email: editFormData.email,
        isVerified: editFormData.isVerified,
        isVipPlus: editFormData.isVipPlus
      }).then((res) => {
        if (res?.status === 200) {
          console.log(editFormData.isVipPlus ? "User upgraded to VIP+ tier" : editFormData.isVerified ? "User upgraded to VIP tier" : "User downgraded to free tier");
          getUsersData(); // Refresh data after successful update
        } else {
          console.log("User tier update failed");
          alert("Failed to update user tier. Please try again.");
        }
      }).catch(err => {
        console.error("Error updating user tier:", err);
        alert("Error updating user tier. Please try again.");
      });
    }
    if(editFormData && (editFormData.firstName !== user.firstName || editFormData.lastName !== user.lastName)){
      updateUser({
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
      }, editUserId).then((res) => {
        if (res?.message === "User updated succcessfully!") {
          console.log("User updated");
          getUsersData(); // Refresh data after successful update
        } else {
          console.log("User not updated");
          alert("Failed to update user information. Please try again.");
        }
      });
    }
    
    // Update local state and exit edit mode
    setUsers(users.map((u) => (u._id === editUserId ? { ...u, ...editFormData } : u)));
    setEditUserId(null);
  }

  const getUsersData = useCallback(() => {
    setLoading(true)
    console.log("DEBUG - Attempting to fetch users");
    const payload = ``
    getUsers(payload).then((res) => {
      setLoading(false)
      console.log("DEBUG - Users API response in component:", res);
      if (res?.message === "Success") {
        if (res?.data && res?.data.length > 0) {
          console.log("DEBUG - Setting users data:", res.data.length, "users found");
          setUsers(res?.data)
        } else {
          console.log("DEBUG - No users data found in response");
          setUsers([])
        }
      } else {
        console.log("DEBUG - Error or no success response:", res?.message);
        setUsers([])
      }
    }).catch(error => {
      setLoading(false);
      console.error("DEBUG - Error fetching users in component:", error);
      setUsers([]);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredUsers = users?.filter(
    (user) =>
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
  )


  //add pagination
  const usersPerPage = 10 // Adjust as needed
  const [currentPage, setCurrentPage] = useState(1)

  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  // Reset to the first page whenever the search term changes so searching
  // while on a later page doesn't land on an empty table.
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const indexOfLastUser = currentPage * usersPerPage
  const indexOfFirstUser = indexOfLastUser - usersPerPage
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser)

  // Helper function to get formatted deposit amount
  const getDepositAmount = (deposit) => {
    if (!deposit) return 0;
    return typeof deposit === 'object' ? (deposit.amount || 0) : deposit;
  }

  const renderPagination = () => (
    <div className={paginationStyles.pagination}>
      <button
        className={paginationStyles.paginationButton}
        onClick={() => paginate(1)}
        disabled={currentPage === 1}
      >
        First
      </button>
      <button
        className={paginationStyles.paginationButton}
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Prev
      </button>
      {[...Array(totalPages)].map((_, index) => {
        if (
          index + 1 === 1 ||
          index + 1 === totalPages ||
          (index + 1 >= currentPage - 2 && index + 1 <= currentPage + 2)
        ) {
          return (
            <button
              key={index}
              className={`${paginationStyles.paginationButton} ${
                currentPage === index + 1 ? paginationStyles.activePage : ''
              }`}
              onClick={() => paginate(index + 1)}
            >
              {index + 1}
            </button>
          );
        }
        return null;
      })}
      <button
        className={paginationStyles.paginationButton}
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </button>
      <button
        className={paginationStyles.paginationButton}
        onClick={() => paginate(totalPages)}
        disabled={currentPage === totalPages}
      >
        Last
      </button>
    </div>
  );

  const renderMobileCards = () => (
    <div className={classes.mobileCards}>
      {currentUsers.map((user) => (
        <div key={user.id} className={classes.mobileCard}>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>First Name</span>
            <span className={classes.cardValue}>{user.firstName}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Last Name</span>
            <span className={classes.cardValue}>{user.lastName}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Email</span>
            <span className={classes.cardValue}>{user.email}{user.isGuest ? " (guest account)" : ""}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Account Tier</span>
            <span className={classes.cardValue}>{user.isVipPlus ? "VIP+" : user.isVerified ? "VIP" : "Free"}</span>
          </div>
          <div className={classes.cardRow}>
            <span className={classes.cardLabel}>Deposit</span>
            <span className={classes.cardValue}>${getDepositAmount(user.deposit)}</span>
          </div>
          <div className={classes.cardActions}>
            <button className={classes.tableButton} onClick={() => handleEditUser(user)}>
              Edit
            </button>
            <button className={classes.tableButton} onClick={() => openCustomer(user)}>
              Open
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    console.log("DEBUG - UserManagement mounted, auth state:", {
      isLoggedIn: authCtx.isLoggedIn,
      userData: authCtx.userData ? "exists" : "missing",
      token: localStorage.getItem("token") ? "exists in localStorage" : "missing from localStorage",
      sessionToken: sessionStorage.getItem("token") ? "exists in sessionStorage" : "missing from sessionStorage"
    });

    if (authCtx.isLoggedIn) {
      getUsersData();
    } else {
      console.log("DEBUG - User not logged in, cannot fetch users");
    }
  }, [getUsersData, authCtx.isLoggedIn]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const pageNumbers = [];

  const range = 4; // Number of pages to show before and after the current page

  // Always include first page
  pageNumbers.push(1);

  // Add pages before and after current page
  for (let i = Math.max(2, currentPage - range); i <= Math.min(totalPages - 1, currentPage + range); i++) {
    pageNumbers.push(i);
  }

  // Always include last page
  if (!pageNumbers.includes(totalPages)) {
    pageNumbers.push(totalPages);
  }

  // Insert "..." when there is a gap
  if (pageNumbers[1] > 2) {
    pageNumbers.splice(1, 0, "...");
  }
  if (pageNumbers[pageNumbers.length - 2] < totalPages - 1) {
    pageNumbers.splice(pageNumbers.length - 1, 0, "...");
  }

  return (
    <div className="container">
      <h1 className={classes.h1}>User Management</h1>
      <input
        type="text"
        className={classes.search_users}
        placeholder="Search by name or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {/* Desktop Table View */}
      <div className={classes.desktopView}>
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr className={classes.tableHeaderRow}>
                <th className={classes.tableHead}>First Name</th>
                <th className={classes.tableHead}>Last Name</th>
                <th className={classes.tableHead}>Email</th>
                <th className={classes.tableHead}>Account Tier</th>
                <th className={classes.tableHead}>Deposit</th>
                <th className={classes.tableHead}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.id} className={classes.tableRow}>
                  <td className={classes.tableData}>{user.firstName}</td>
                  <td className={classes.tableData}>{user.lastName}</td>
                  {/* Opened by staff for a dealer customer; theirs when they sign up. */}
                  <td className={classes.tableData}>{user.email}{user.isGuest && <small> (guest account)</small>}</td>
                  <td className={classes.tableData}>
                    {editUserId === user._id ? (
                      <div className={classes.editField}>
                        <label className={classes.checkboxLabel}>
                          <input
                            type="checkbox"
                            name="isVerified"
                            checked={editFormData.isVerified}
                            onChange={handleEditFormChange}
                          />
                          VIP Tier
                        </label>
                      </div>
                    ) : (
                      user.isVipPlus ? "VIP+" : user.isVerified ? "VIP" : "Free"
                    )}
                  </td>
                  <td className={classes.tableData}>
                    {editUserId === user._id ? (
                      <div className={classes.editField}>
                        <input
                          type="number"
                          name="deposit"
                          value={editFormData.deposit || 0}
                          onChange={handleEditFormChange}
                          className={classes.inputField}
                        />
                      </div>
                    ) : (
                      `$${getDepositAmount(user.deposit)}`
                    )}
                  </td>
                  <td className={classes.tableData}>
                    {editUserId === user._id ? (
                      <div className={classes.editActions}>
                        <button 
                          onClick={handleSaveChanges} 
                          className={classes.saveButton}
                          type="button"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditUserId(null)}
                          className={classes.cancelButton}
                          type="button"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          className={classes.tableButton}
                          onClick={() => handleEditUser(user)}
                          type="button"
                        >
                          Edit
                        </button>
                        <button
                          className={classes.tableButton}
                          onClick={() => openCustomer(user)}
                          type="button"
                          title="Their leads, inspections, purchases, billing and history"
                        >
                          Open
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards View */}
      <div className={classes.mobileView}>
        {renderMobileCards()}
      </div>

      {renderPagination()}

      {/* Edit Form Modal */}
      {editUserId && (
        <div className={classes.modal}>
          <div className={classes.modalContent}>
            <h2 className={classes.headingH2}>Edit User</h2>
            <form className={classes.modalForm} onSubmit={(e) => e.preventDefault()}>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>First Name</label>
                <input
                  type="text"
                  className={classes.formInput}
                  name="firstName"
                  value={editFormData.firstName}
                  onChange={handleEditFormChange}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Last Name</label>
                <input
                  type="text"
                  className={classes.formInput}
                  name="lastName"
                  value={editFormData.lastName}
                  onChange={handleEditFormChange}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Email</label>
                {/* Read-only: the tier update (verifyUser) is keyed on this email,
                    so it must always reflect the real user being edited. */}
                <input
                  type="email"
                  className={classes.formInput}
                  name="email"
                  value={editFormData.email}
                  readOnly
                  title="Email cannot be changed here"
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Account Tier</label>
                <select
                  className={classes.formInput}
                  name="tier"
                  value={editFormData.isVipPlus ? "vip_plus" : editFormData.isVerified ? "vip" : "free"}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditFormData({
                      ...editFormData,
                      isVipPlus: val === "vip_plus",
                      isVerified: val === "vip" || val === "vip_plus",
                    });
                  }}
                >
                  <option className={classes.selectOption} value="vip_plus">
                    VIP+
                  </option>
                  <option className={classes.selectOption} value="vip">
                    VIP
                  </option>
                  <option className={classes.selectOption} value="free">
                    Free
                  </option>
                </select>
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Deposit Amount</label>
                <input
                  type="number"
                  className={classes.formInput}
                  name="deposit"
                  value={editFormData.deposit || 0}
                  onChange={handleEditFormChange}
                />
              </div>
              <div className={classes.formButtons}>
                <button 
                  className={classes.formButtonPrimary} 
                  onClick={handleSaveChanges}
                  type="button"
                >
                  Save Changes
                </button>
                <button 
                  className={classes.formButtonSecondary} 
                  onClick={() => setEditUserId(null)}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement

